import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@workspace/ui/lib/utils';

import { LanguageChip } from '../LanguageChip';
import { LanguageMenu } from './LanguageMenu';
import type { LanguageItem } from './types';

/** Gap between the chip and the popover, in pixels */
const POPOVER_OFFSET = 8;

/** Space kept between the popover and the bottom of the viewport, in pixels */
const POPOVER_VIEWPORT_MARGIN = 16;

const POPOVER_ID = 'language-picker-popover';

/** Language switcher: a chip that opens a popover with the available languages */
export interface LanguagePickerProps {
  items: LanguageItem[];
  /** Locale code of the active language */
  currentLang: string;
  onSelect: (lang: string) => void;
  /** Accessible name for the chip */
  ariaLabel?: string;
  /** Analytics attribute forwarded to the chip */
  dataButton?: string;
  className?: string;
}

/** Popover coordinates, anchored to the chip's bottom-right corner */
interface PopoverPosition {
  top: number;
  right: number;
  maxHeight: number;
}

export const LanguagePicker = ({
  items,
  currentLang,
  onSelect,
  ariaLabel,
  dataButton,
  className,
}: LanguagePickerProps) => {
  const chipRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  const isOpen = position !== null;
  const activeItem = items.find((item) => item.code === currentLang);

  const close = () => setPosition(null);

  // The chip lives inside the horizontally scrollable header row, which would
  // clip an absolutely positioned popover, so the popover is portalled to the
  // body and anchored to the chip's measured position. `clientWidth` rather
  // than `innerWidth`: the latter includes a classic scrollbar, which the
  // containing block of a fixed element does not.
  const handleToggle = () => {
    if (isOpen) {
      close();
      return;
    }

    const rect = chipRef.current?.getBoundingClientRect();
    if (!rect) return;

    const top = rect.bottom + POPOVER_OFFSET;

    setPosition({
      top,
      right: document.documentElement.clientWidth - rect.right,
      maxHeight: Math.max(
        window.innerHeight - top - POPOVER_VIEWPORT_MARGIN,
        0,
      ),
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        chipRef.current?.focus();
        return;
      }

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

      const options = Array.from(
        popoverRef.current?.querySelectorAll<HTMLElement>(
          '[role="menuitemradio"]',
        ) ?? [],
      );
      if (!options.length) return;

      e.preventDefault();
      const current = options.indexOf(document.activeElement as HTMLElement);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = (current + step + options.length) % options.length;
      options[next]?.focus();
    };

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        chipRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }

      close();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const menu = popoverRef.current;
    const checked = menu?.querySelector<HTMLElement>('[aria-checked="true"]');
    (
      checked ?? menu?.querySelector<HTMLElement>('[role="menuitemradio"]')
    )?.focus();
  }, [isOpen]);

  if (items.length < 2) return null;

  const handleSelect = (lang: string) => {
    close();
    if (lang !== currentLang) onSelect(lang);
  };

  return (
    <>
      <LanguageChip
        ref={chipRef}
        data-button={dataButton}
        aria-label={ariaLabel}
        aria-controls={POPOVER_ID}
        label={activeItem?.title ?? currentLang.toUpperCase()}
        flag={activeItem?.img}
        isOpen={isOpen}
        onClick={handleToggle}
        className={className}
      />

      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            id={POPOVER_ID}
            style={{
              top: position.top,
              right: position.right,
              maxHeight: position.maxHeight,
            }}
            className={cn(
              'fixed z-1000 min-w-[180px] overflow-y-auto rounded-5 bg-bg-content p-2',
              'shadow-[0_4px_16px_-2px_rgba(16,24,40,0.16)]',
            )}
          >
            <LanguageMenu
              items={items}
              currentLang={currentLang}
              onSelect={handleSelect}
            />
          </div>,
          document.body,
        )}
    </>
  );
};
